import React, { useState, useEffect, useCallback } from 'react';
import { returnService, productService, supermercadoStockService, extractErrorDetails } from '../services/api';
import BarcodeScanner from './BarcodeScanner';
import ErrorDisplay from './ErrorDisplay';

function CreateReturn({ onReturnCreated, userSupermercadoId }) {
  const [descripcion, setDescripcion] = useState('');
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [supermercadoStock, setSupermercadoStock] = useState({});
  const [showScanner, setShowScanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [success, setSuccess] = useState('');

  // Load all active products and supermercado stock on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingProducts(true);
        
        // Load products
        const productsResponse = await productService.getAllActiveProducts();
        if (productsResponse.success) {
          setAllProducts(productsResponse.data || []);
        }
        
        // Load supermercado stock
        if (userSupermercadoId) {
          const stockResponse = await supermercadoStockService.getMyStock();
          console.log('[CreateReturn] Loaded stock for', stockResponse.data?.length || 0, 'products');
          if (stockResponse.success && Array.isArray(stockResponse.data)) {
            const stockMap = {};
            stockResponse.data.forEach(stock => {
              // Ensure key is consistent type - use string keys for reliability
              const key = String(stock.productId);
              const stockValue = parseInt(stock.stock, 10) || 0;
              stockMap[key] = stockValue;
            });
            console.log('[CreateReturn] Stock map created with', Object.keys(stockMap).length, 'entries');
            setSupermercadoStock(stockMap);
          } else {
            console.warn('[CreateReturn] Failed to load stock:', {
              success: stockResponse?.success,
              isArray: Array.isArray(stockResponse?.data)
            });
          }
        }
      } catch (err) {
        console.error('[DEBUG] Error loading data:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadData();
  }, [userSupermercadoId]);

  // Local search filter - filters from preloaded products
  const handleSearch = useCallback((query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const filtered = allProducts.filter(product => 
      product.nombre.toLowerCase().includes(lowerQuery) ||
      product.codigoBarras.toLowerCase().includes(lowerQuery)
    );
    setSearchResults(filtered.slice(0, 10)); // Limit to 10 results
  }, [allProducts]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  const handleScan = async (barcode) => {
    setShowScanner(false);
    setError('');
    setErrorTrace(null);
    setDebugInfo(null);
    try {
      const response = await productService.getProductByBarcode(barcode);
      if (response.success && response.data) {
        addProductToReturn(response.data);
      } else {
        setError(response.message || 'Producto no encontrado con ese código de barras');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Producto no encontrado con ese código de barras');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    }
  };

  const addProductToReturn = (product) => {
    const existingIndex = items.findIndex(item => item.productId === product.id);
    
    if (existingIndex >= 0) {
      // Increment quantity if already exists
      const newItems = [...items];
      newItems[existingIndex].cantidad += 1;
      setItems(newItems);
    } else {
      // Add new item with stock info
      // Use string keys for consistent lookup
      const productKey = String(product.id);
      const stockDisponible = supermercadoStock[productKey] || 0;
      console.log('[CreateReturn] Adding product:', product.nombre, 'Stock:', stockDisponible);
      if (stockDisponible === 0 && Object.keys(supermercadoStock).length > 0) {
        console.warn('[CreateReturn] Product has no stock. ProductId:', product.id, 'Available stock keys:', Object.keys(supermercadoStock).slice(0, 5));
      }
      setItems([...items, {
        productId: product.id,
        codigoBarras: product.codigoBarras,
        nombreProducto: product.nombre,
        cantidad: 1,
        stockDisponible: stockDisponible
      }]);
    }
    
    setSearchQuery('');
    setSearchResults([]);
    setError('');
  };

  const removeItem = (productId) => {
    setItems(items.filter(item => item.productId !== productId));
  };

  const updateItemQuantity = (productId, cantidad) => {
    console.log('[DEBUG] Actualizando cantidad:', { productId, cantidad, tipoDeCantidad: typeof cantidad });
    
    const newItems = items.map(item => {
      if (item.productId === productId) {
        // Allow empty string for editing
        if (cantidad === '') {
          console.log('[DEBUG] Cantidad vacía, manteniendo para edición');
          return { ...item, cantidad: '' };
        }
        const numValue = parseInt(cantidad, 10);
        const finalValue = isNaN(numValue) ? 1 : Math.max(1, numValue);
        console.log('[DEBUG] Cantidad procesada:', { 
          original: cantidad, 
          parsed: numValue, 
          final: finalValue,
          esNaN: isNaN(numValue)
        });
        return { ...item, cantidad: finalValue };
      }
      return item;
    });
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorTrace(null);
    setDebugInfo(null);
    setSuccess('');

    if (!descripcion.trim()) {
      setError('Por favor, ingrese una descripción');
      return;
    }

    if (items.length === 0) {
      setError('Debe agregar al menos un producto');
      return;
    }

    setLoading(true);

    try {
      const returnData = {
        descripcion,
        items: items.map(item => ({
          productId: item.productId,
          cantidad: item.cantidad
        }))
      };

      console.log('[DEBUG] Enviando datos de devolución:', JSON.stringify(returnData, null, 2));
      console.log('[DEBUG] Validación de items:', items.map(item => ({
        productId: item.productId,
        nombreProducto: item.nombreProducto,
        cantidad: item.cantidad,
        tipoDeCantidad: typeof item.cantidad,
        esNumero: !isNaN(item.cantidad),
        esMayorQueCero: item.cantidad > 0,
        valorOriginal: item.cantidad
      })));

      const response = await returnService.createReturn(returnData);

      console.log('[DEBUG] Respuesta del servidor:', response);

      if (response.success) {
        setSuccess('Devolución creada exitosamente');
        setDescripcion('');
        setItems([]);
        if (onReturnCreated) {
          onReturnCreated(response.data);
        }
      } else {
        console.error('[DEBUG] Error en respuesta:', response);
        setError(response.message || 'Error al crear la devolución');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      console.error('[DEBUG] Error completo al crear devolución:', err);
      console.error('[DEBUG] Detalles del error:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al crear la devolución');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setLoading(false);
    }
  };

  const getTotalItems = () => {
    return items.reduce((sum, item) => sum + item.cantidad, 0);
  };

  return (
    <div className="create-order-container">
      {showScanner && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      <div className="create-order-layout">
        {/* Product Search Section */}
        <div className="product-search-panel">
          <h3>Buscar Productos</h3>
          <p style={{fontSize: '0.9em', color: '#666'}}>
            Stock cargado: {Object.keys(supermercadoStock).length} productos
          </p>
          
          {loadingProducts ? (
            <p className="loading-text">Cargando productos...</p>
          ) : (
            <>
              <div className="search-input-container">
                <div className="input-with-button">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por código o nombre..."
                    className="search-input"
                    disabled={loading}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowScanner(true)}
                    className="btn btn-icon"
                    title="Escanear código de barras"
                    disabled={loading}
                  >
                    📷
                  </button>
                </div>
              </div>

              {searchResults.length > 0 && (
                <div className="product-results">
                  <h4>Resultados ({searchResults.length})</h4>
                  <ul className="product-list">
                    {searchResults.map(product => {
                      // Use string keys for consistent lookup
                      const productKey = String(product.id);
                      const stockDisponible = supermercadoStock[productKey] || 0;
                      return (
                        <li 
                          key={product.id}
                          onClick={() => addProductToReturn(product)}
                          className="product-item"
                        >
                          <span className="product-code">{product.codigoBarras}</span>
                          <span className="product-name">{product.nombre}</span>
                          <span className={`stock-info ${stockDisponible > 0 ? 'positive' : 'zero'}`}>
                            Stock: {stockDisponible}
                          </span>
                          <button 
                            type="button" 
                            className="btn btn-small btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              addProductToReturn(product);
                            }}
                          >
                            + Agregar
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {searchQuery.length >= 2 && searchResults.length === 0 && (
                <p className="no-results">No se encontraron productos</p>
              )}

              {searchQuery.length === 0 && (
                <p className="search-hint">Escriba al menos 2 caracteres para buscar</p>
              )}
            </>
          )}
        </div>

        {/* Return Form Section */}
        <div className="order-form-panel">
          <h3>Devolución de Productos</h3>
          
          <form onSubmit={handleSubmit} className="order-form">
            <div className="input-group">
              <label htmlFor="descripcion">Descripción de la Devolución*</label>
              <textarea
                id="descripcion"
                name="descripcion"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Motivo de la devolución (ej: Productos vencidos, Productos defectuosos, etc.)"
                rows="2"
                required
                disabled={loading}
              />
            </div>

            {items.length > 0 ? (
              <div className="order-items">
                <div className="order-items-header">
                  <h4>Productos a Devolver ({items.length})</h4>
                  <span className="total-units">
                    Total: {getTotalItems()} unidades
                  </span>
                </div>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Producto</th>
                      <th>Stock</th>
                      <th>Cantidad</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.productId}>
                        <td>{item.codigoBarras}</td>
                        <td>{item.nombreProducto}</td>
                        <td>
                          <span className={`stock-info ${item.stockDisponible > 0 ? 'positive' : 'zero'}`}>
                            {item.stockDisponible}
                          </span>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            value={item.cantidad}
                            onChange={(e) => updateItemQuantity(item.productId, e.target.value)}
                            onBlur={(e) => {
                              // Ensure valid quantity on blur
                              if (e.target.value === '' || parseInt(e.target.value) < 1) {
                                updateItemQuantity(item.productId, '1');
                              }
                            }}
                            disabled={loading}
                            className="quantity-input"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => removeItem(item.productId)}
                            className="btn btn-small btn-danger"
                            disabled={loading}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-cart">
                <p>No hay productos en la devolución</p>
                <p className="hint">Use la búsqueda para agregar productos</p>
              </div>
            )}

            <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />
            {success && <p className="success-message">{success}</p>}

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || items.length === 0}
              >
                {loading ? 'Creando...' : 'Crear Devolución'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateReturn;
